/**
 * Device fingerprinting utility for anti-abuse protection
 * OPTIMIZED VERSION: Fast fingerprint generation with caching
 * Generates a unique identifier based on browser and device characteristics
 */

class DeviceFingerprint {
  private static instance: DeviceFingerprint;
  private fingerprint: string | null = null;
  private fingerprintPromise: Promise<string> | null = null;

  static getInstance(): DeviceFingerprint {
    if (!DeviceFingerprint.instance) {
      DeviceFingerprint.instance = new DeviceFingerprint();
    }
    return DeviceFingerprint.instance;
  }

  // Pre-generate fingerprint on first access (don't wait for checkout)
  async generateFingerprint(): Promise<string> {
    // Return cached fingerprint immediately
    if (this.fingerprint) {
      return this.fingerprint;
    }

    // If already generating, return the existing promise
    if (this.fingerprintPromise) {
      return this.fingerprintPromise;
    }

    // Generate new fingerprint
    this.fingerprintPromise = this.doGenerateFingerprint();
    return this.fingerprintPromise;
  }

  private async doGenerateFingerprint(): Promise<string> {
    try {
      // FAST fingerprint - only use quick, synchronous data
      // Skip slow operations like canvas, webgl, and font detection
      const quickData = {
        ua: navigator.userAgent,
        lang: navigator.language,
        platform: navigator.platform,
        screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        cores: navigator.hardwareConcurrency || 0,
        memory: (navigator as any).deviceMemory || 0,
        touch: navigator.maxTouchPoints || 0,
      };

      // Fast hash using simple string operations
      const dataString = JSON.stringify(quickData);
      this.fingerprint = await this.fastHash(dataString);
      return this.fingerprint;
    } catch {
      // Ultra-fast fallback
      const fallback = `${navigator.userAgent}-${screen.width}-${screen.height}`;
      this.fingerprint = await this.fastHash(fallback);
      return this.fingerprint;
    }
  }

  private async fastHash(data: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Fallback to simple hash
      let hash = 0;
      for (let i = 0; i < data.length; i++) {
        const char = data.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return Math.abs(hash).toString(16);
    }
  }

  // Get cached fingerprint or generate new one
  async getFingerprint(): Promise<string> {
    return this.generateFingerprint();
  }

  // Clear cached fingerprint (useful for testing)
  clearCache(): void {
    this.fingerprint = null;
    this.fingerprintPromise = null;
  }
}

export const deviceFingerprint = DeviceFingerprint.getInstance();

// Export convenience function
export const getDeviceFingerprint = (): Promise<string> => {
  return deviceFingerprint.getFingerprint();
};

// Pre-generate fingerprint on module load (runs in background)
// This ensures fingerprint is ready when checkout is clicked
if (typeof window !== 'undefined') {
  // Use requestIdleCallback for better performance
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      deviceFingerprint.generateFingerprint();
    }, { timeout: 1000 });
  } else {
    setTimeout(() => {
      deviceFingerprint.generateFingerprint();
    }, 100);
  }
}
